/* eslint-disable react-hooks/exhaustive-deps */
import AgGridWrapper from '../../../../../Common/BaseComponentWrappers/AgGridWrapper';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import styled from 'styled-components';
import {labels, valueComparator, getFrameEssentialQuery} from '../../../../../../util/utils';
import {useDispatch, useSelector} from 'react-redux';
import LoadingIndicator from '../../../../../Common/LoadingIndicator/LoadingIndicator';
import {
	EngagementCommentStatus,
	formBodyType,
	frameSubEvent,
	serviceURL,
	specialBodyTypes
} from '../../../../../../util/uiconstants';
import Env from '../../../../../../util/env';
import {getAllDocuments} from '../../../../../../actions/Document/documentactions';
import {useEngagementId, useQuery} from '../../../../../../util/customHooks';
import {getHeaderById, getSectionById, getTaskDocumentByDocumentId} from '../../../../../../selectors/Ares/selectors';
import {getBodiesBySectionId} from '../../../../../../actions/Ares/compareFormsActions';
import AGGridRichTextCellRenderer from './DrillDown/CellRenderer/AGGridRichTextCellRenderer';
import {groupBy, orderBy} from 'lodash';
import AGGridCountOfResponsesCellRenderer from './DrillDown/CellRenderer/AGGridCountOfResponsesCellRenderer';
import FormNavigationButton from './FormNavigationButtonV2';
import {useIsDrawerMode} from '../../../../../../util/Ares/customHooks';
import {getFormBodiesBySectionId} from '../../../../../../actions/FormData/formdataactions';
import AGGridSliderCellRenderer from './DrillDown/CellRenderer/AGGridSliderCellRenderer';
import AGGridLinkCellRenderer from './DrillDown/CellRenderer/AGGridLinkCellRenderer';
import {
	frameCommunicationEvents,
	useFrameService,
	useFrameKey,
	useFrameData,
	frameStoreEvent
} from '@ey/canvascoreservices';
import Utility from '../../../../../../util/utilityfunctions';
import CustomHeader from './DrillDown/CustomHeader';
import EllipsesControl from '../../../../../../components/Common/EllipsesControl/EllipsesControl';
import ReactiveTabRenamer from '../../../../../../components/Common/Modal/ReactiveTabRenamer';
import noRowsOverlayComponent from './DrillDown/NoRowsOverlay';
import AGGridCommentCountCellRenderer from './DrillDown/CellRenderer/AGGridCommentCountCellRenderer';
import Icon from '../../../../../Common/BaseComponentWrappers/MotifIcon';
import { MotifImageIcFilterNone24px } from '@ey-xd/motif-icon';

function CompareResponsesV2DrillDownContainer() {
	const {documentId, headerId, sectionId} = useParams();

	const query = useQuery();

	const engagementId = useEngagementId();

	const isDrawerMode = useIsDrawerMode();

	const dispatch = useDispatch();

	const formData = useSelector((store) => getTaskDocumentByDocumentId(store, documentId));

	const compare = useSelector((store) => store.aresCompareBodies);

	const section = useSelector((store) => getSectionById(store, Number(sectionId)));

	const header = useSelector((store) => getHeaderById(store, Number(headerId)));

	const [isLoading, setIsLoading] = useState(true);

	const navigate = useNavigate();

	const frameService = useFrameService();

	const frameData = useFrameData();

	const subscriptionRef = useRef(null);

	const {frameKey} = useFrameKey();

	const [documentIds, setDocumentIds] = useState([]);

	useEffect(() => {
		subscriptionRef.current = frameService.subscribeFrame({
			next: (args) => {
				switch (args.event) {
					case frameCommunicationEvents.MESSAGE:
						switch (args.subEvent) {
							case frameSubEvent.COMPARE_SECTION_CHANGED: {
								const url = Utility.getAresTaskListNavigationUrl(
									args.documentId,
									args.headerId,
									args.sectionId,
									args.engagementId,
									args.taskId,
									getFrameEssentialQuery(args.queryParameters),
									args.documentTypeId,
									false,
									true
								);

								setDocumentIds(args.documentIds);

								navigate(url);

								break;
							}

							case frameStoreEvent.DATA: {
								if (args.key === frameKey) {
									setDocumentIds(args.data.documentIds);
								}

								break;
							}
						}
				}
			}
		});

		frameData.getData(frameKey).then((data) => {
			setDocumentIds(data?.documentIds);
		});

		return () => {
			if (subscriptionRef.current && !subscriptionRef.current.closed) {
				subscriptionRef.current.unsubscribe();
			}
		};
	}, []);

	useEffect(() => {
		if (formData && formData.knowledgeFormId) {
			const uri = `Documents?filterKnowledgeForm=${formData.knowledgeFormId}&includeRelatedTasks=true&includeRelatedEntities=true`;

			dispatch(getAllDocuments(`${Env.getBaseURL(serviceURL, engagementId)}${uri}`));
		}
	}, [formData, engagementId, dispatch]);

	useEffect(() => {
		if (documentIds && documentIds.length > 0) {
			const data = {
				documentIDs: documentIds.includes(documentId) ? documentIds : [documentId, ...documentIds]
			};

			setIsLoading(true);

			dispatch(getBodiesBySectionId({engagementId, documentId, sectionId, data}))
				.then((response) => {
					const sliderBodies = response.formBodies.filter((body) => body.formBodyTypeId === formBodyType.Slider);

					if (sliderBodies.length > 0) {
						const uri = `forms/${documentId}/headers/${headerId}/sections/${sectionId}/Bodies`;

						return dispatch(getFormBodiesBySectionId(`${Env.getBaseURL(serviceURL, engagementId)}${uri}`));
					}
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, [documentIds, documentId, engagementId, sectionId, headerId, dispatch]);

	const textResponseBodies = useMemo(() => {
		return [
			formBodyType.SingleLineInput,
			formBodyType.MultiLineInput,
			formBodyType.InlineRichText,
			formBodyType.TextResponse
		];
	}, []);

	const hiddenBodyTypes = useMemo(() => {
		return [
			formBodyType.CheckboxViewport,
			formBodyType.RadioVerticalViewport,
			formBodyType.KnowledgeOnlyViewport,
			formBodyType.TextResponseViewport
		];
	}, []);

	const bodyTypesWithLinkRenderer = useMemo(() => {
		return [...textResponseBodies, ...specialBodyTypes];
	}, [textResponseBodies]);

	const rowData = useMemo(() => {
		if (compare && compare.formBodies && formData) {
			const data = [];

			const visibleBodies = compare.formBodies.filter((body) => !hiddenBodyTypes.includes(body.formBodyTypeId));

			const groupByKnowledgeFormBodyId = groupBy(visibleBodies, 'knowledgeFormBodyId');

			const bodies = [];

			for (const [knowledgeFormBodyId, formBodies] of Object.entries(groupByKnowledgeFormBodyId)) {
				const hasOptions = formBodies.filter((body) => body.hasOptions).length > 0;

				const formBody = formBodies[0];

				const associatedDocuments = formBodies.map((body) => {
					return {
						documentId: body.documentId,
						documentTypeId: formData.documentTypeId,
						formHeaderId: body.formHeaderId,
						formSectionId: body.formSectionId,
						formBodyId: body.formBodyId,
						formBodyTypeId: body.formBodyTypeId,
						hasOpenComments: body.openCommentCount > 0,
						hasClearedComments: body.clearedCommentCount > 0
					};
				});

				if (hasOptions) {
					const bodySelectedOptions = [];
					const bodiesWithoutResponses = [];

					formBodies.forEach((body) => {
						if (Array.isArray(body.bodySelectedOptions) && body.bodySelectedOptions.length > 0) {
							const options = body.bodySelectedOptions.map((option) => {
								return {
									...option,
									documentId: body.documentId,
									openCommentCount: body.openCommentCount,
									clearedCommentCount: body.clearedCommentCount
								};
							});

							bodySelectedOptions.push(...options);
						} else {
							bodiesWithoutResponses.push(body);
						}
					});

					const groupByKnowledgeFormBodyOptionId = groupBy(bodySelectedOptions, 'knowledgeFormBodyOptionId');

					bodies.push({
						rowSpan:
							Object.entries(groupByKnowledgeFormBodyOptionId).length + (bodiesWithoutResponses.length > 0 ? 1 : 0),
						documentId: formBody.documentId,
						documentTypeId: formData.documentTypeId,
						formHeaderId: formBody.formHeaderId,
						formSectionId: formBody.formSectionId,
						formBodyId: formBody.formBodyId,
						formBodyTypeId: formBody.formBodyTypeId,
						formBodyDescription: formBody.formBodyDescription,
						knowledgeFormBodyId,
						knowledgeDisplayOrder: formBody.knowledgeDisplayOrder,
						bodySelectedOptions,
						bodiesWithoutResponses,
						associatedDocuments
					});
				} else {
					const totalOpenCommentCount = formBodies.reduce((acc, item) => acc + (item?.openCommentCount || 0), 0);
					const totalClearedCommentCount = formBodies.reduce((acc, item) => acc + (item?.clearedCommentCount || 0), 0);

					const formBodyFreeTextResponses = formBodies.filter((body) => body.formBodyFreeText).sort(valueComparator);

					if (formBodyFreeTextResponses.length > 0) {
						bodies.push({
							rowSpan: formBodyFreeTextResponses.length,
							documentId: formBody.documentId,
							documentTypeId: formData.documentTypeId,
							formHeaderId: formBody.formHeaderId,
							formSectionId: formBody.formSectionId,
							formBodyId: formBody.formBodyId,
							formBodyTypeId: formBody.formBodyTypeId,
							formBodyDescription: formBody.formBodyDescription,
							knowledgeFormBodyId,
							knowledgeDisplayOrder: formBody.knowledgeDisplayOrder,
							formBodyFreeTextResponses,
							associatedDocuments,
							openCommentCount: totalOpenCommentCount,
							clearedCommentCount: totalClearedCommentCount
						});
					} else {
						bodies.push({
							rowSpan: 1,
							documentId: formBody.documentId,
							documentTypeId: formData.documentTypeId,
							formHeaderId: formBody.formHeaderId,
							formSectionId: formBody.formSectionId,
							formBodyId: formBody.formBodyId,
							formBodyTypeId: formBody.formBodyTypeId,
							formBodyDescription: formBody.formBodyDescription,
							knowledgeFormBodyId,
							knowledgeDisplayOrder: formBody.knowledgeDisplayOrder,
							associatedDocuments,
							openCommentCount: totalOpenCommentCount,
							clearedCommentCount: totalClearedCommentCount
						});
					}
				}
			}

			orderBy(bodies, 'knowledgeDisplayOrder', 'asc').forEach((body, idx) => {
				const rowType = (idx + 1) % 2 === 0 ? 'even' : 'odd';

				let description = body.formBodyDescription;

				const hasDescription = typeof description === 'string';

				if (!hasDescription) {
					description = {
						clickable: labels.click,
						label: labels.clickToViewQuestionAnswer
					};
				} else {
					description = `${description}`;
				}

				if (body?.bodySelectedOptions?.length > 0) {
					const groupByKnowledgeFormBodyOptionId = groupBy(body.bodySelectedOptions, 'knowledgeFormBodyOptionId');

					let index = 0;

					const entries = Object.entries(groupByKnowledgeFormBodyOptionId);

					const hasBodiesWithoutResponse = body?.bodiesWithoutResponses?.length > 0;

					for (const [knowledgeFormBodyOptionId, selectedOptions] of entries) {
						const selectedOptionDocumentIds = selectedOptions.map((option) => option.documentId);

						const associatedDocuments = body.associatedDocuments.filter((doc) =>
							selectedOptionDocumentIds.includes(doc.documentId)
						);

						const bodiesWithSeletedOptions = selectedOptions.filter((option) =>
							selectedOptionDocumentIds.includes(option.documentId)
						);
						const totalOpenCommentCount = bodiesWithSeletedOptions.reduce(
							(acc, item) => acc + (item?.openCommentCount || 0),
							0
						);
						const totalClearedCommentCount = bodiesWithSeletedOptions.reduce(
							(acc, item) => acc + (item?.clearedCommentCount || 0),
							0
						);

						data.push({
							rowSpan: index === 0 ? body.rowSpan : 1,
							rowType,
							engagementId,
							documentId: body.documentId,
							documentTypeId: body.documentTypeId,
							compareSectionId: Number(sectionId),
							formHeaderId: body.formHeaderId,
							formSectionId: body.formSectionId,
							formBodyId: body.formBodyId,
							formBodyTypeId: body.formBodyTypeId,
							sectionName: section?.name,
							knowledgeFormBodyOptionId,
							knowledgeFormBodyId: body.knowledgeFormBodyId,
							description: index === 0 ? description : null,
							hasDescription,
							answers: selectedOptions[0].formBodyOptionText,
							countOfResponses: selectedOptions.length,
							percentageOfResponses: Math.round((selectedOptions.length / body.associatedDocuments.length) * 100),
							openCommentCount: totalOpenCommentCount,
							clearedCommentCount: totalClearedCommentCount,
							associatedDocuments,
							isLast: !hasBodiesWithoutResponse ? entries.length === index + 1 : false
						});

						index++;
					}

					if (body?.bodiesWithoutResponses?.length > 0) {
						const ids = body.bodiesWithoutResponses.map((body) => body.documentId);

						const associatedDocuments = body.associatedDocuments.filter((doc) => ids.includes(doc.documentId));
						const bodiesWithoutResponses = body.bodiesWithoutResponses.filter((option) =>
							ids.includes(option.documentId)
						);
						const totalOpenCommentCount = bodiesWithoutResponses.reduce(
							(acc, item) => acc + (item?.openCommentCount || 0),
							0
						);
						const totalClearedCommentCount = bodiesWithoutResponses.reduce(
							(acc, item) => acc + (item?.clearedCommentCount || 0),
							0
						);

						data.push({
							rowSpan: 1,
							rowType,
							engagementId,
							documentId: body.documentId,
							documentTypeId: body.documentTypeId,
							compareSectionId: Number(sectionId),
							formHeaderId: body.formHeaderId,
							formSectionId: body.formSectionId,
							formBodyId: body.formBodyId,
							formBodyTypeId: body.formBodyTypeId,
							sectionName: section?.name,
							knowledgeFormBodyId: body.knowledgeFormBodyId,
							description: index === 0 ? description : null,
							hasDescription,
							answers: labels.noAnswerProvided,
							className: 'no-answer-provided',
							countOfResponses: body.bodiesWithoutResponses.length,
							percentageOfResponses: Math.round(
								(body.bodiesWithoutResponses.length / body.associatedDocuments.length) * 100
							),
							openCommentCount: totalOpenCommentCount,
							clearedCommentCount: totalClearedCommentCount,
							associatedDocuments,
							isLast: true
						});
					}
				} else if (body?.bodiesWithoutResponses?.length > 0) {
					const ids = body.bodiesWithoutResponses.map((body) => body.documentId);
					const totalOpenCommentCount = body.bodiesWithoutResponses.reduce(
						(acc, item) => acc + (item?.openCommentCount || 0),
						0
					);
					const totalClearedCommentCount = body.bodiesWithoutResponses.reduce(
						(acc, item) => acc + (item?.clearedCommentCount || 0),
						0
					);
					const associatedDocuments = body.associatedDocuments.filter((doc) => ids.includes(doc.documentId));

					data.push({
						rowSpan: 1,
						rowType,
						engagementId,
						documentId: body.documentId,
						documentTypeId: body.documentTypeId,
						compareSectionId: Number(sectionId),
						formHeaderId: body.formHeaderId,
						formSectionId: body.formSectionId,
						formBodyId: body.formBodyId,
						formBodyTypeId: body.formBodyTypeId,
						sectionName: section?.name,
						knowledgeFormBodyId: body.knowledgeFormBodyId,
						description,
						hasDescription,
						answers: labels.noAnswerProvided,
						className: 'no-answer-provided',
						countOfResponses: body.bodiesWithoutResponses.length,
						percentageOfResponses: Math.round(
							(body.bodiesWithoutResponses.length / body.associatedDocuments.length) * 100
						),
						openCommentCount: totalOpenCommentCount,
						clearedCommentCount: totalClearedCommentCount,
						associatedDocuments,
						isLast: true
					});
				} else {
					const formBodyTypeId = body.formBodyTypeId;

					const showLink = bodyTypesWithLinkRenderer.includes(formBodyTypeId);

					if (body?.formBodyFreeTextResponses?.length > 0 && !showLink) {
						body.formBodyFreeTextResponses.forEach((response, index) => {
							const isLast = body.formBodyFreeTextResponses.length === index + 1;

							data.push({
								rowType,
								rowSpan: index === 0 ? body.rowSpan : 1,
								engagementId,
								documentId: body.documentId,
								documentTypeId: body.documentTypeId,
								compareSectionId: Number(sectionId),
								formHeaderId: body.formHeaderId,
								formSectionId: body.formSectionId,
								formBodyId: body.formBodyId,
								formBodyTypeId,
								sectionName: section?.name,
								knowledgeFormBodyId: body.knowledgeFormBodyId,
								description: index === 0 ? description : null,
								hasDescription,
								isFreeText: index + 1 !== body.formBodyFreeTextResponses.length,
								answers: response.formBodyFreeText,
								countOfResponses: 1,
								percentageOfResponses: Math.round((1 / body.associatedDocuments.length) * 100),
								openCommentCount: body.openCommentCount,
								clearedCommentCount: body.clearedCommentCount,
								associatedDocuments: body.associatedDocuments,
								isLast
							});
						});
					} else {
						let answers = '';

						if (showLink) {
							answers = {
								clickable: labels.click,
								label: labels.clickToViewAnswer
							};
						}

						data.push({
							rowType,
							engagementId,
							documentId: body.documentId,
							documentTypeId: body.documentTypeId,
							compareSectionId: Number(sectionId),
							formHeaderId: body.formHeaderId,
							formSectionId: body.formSectionId,
							formBodyId: body.formBodyId,
							formBodyTypeId,
							sectionName: section?.name,
							description,
							hasDescription,
							answers,
							showLink,
							associatedDocuments: body.associatedDocuments,
							clearedCommentCount: body.clearedCommentCount,
							openCommentCount: body.openCommentCount,
							isLast: true
						});
					}
				}
			});

			return data;
		}

		return null;
	}, [compare, formData?.documentTypeId, engagementId, sectionId, section?.name, bodyTypesWithLinkRenderer]);

	const rowSpan = useCallback((params) => {
		return params.data.rowSpan;
	}, []);

	const columnDefs = useMemo(() => {
		const availableWidth = window.innerWidth - 72;

		const c1 = 340;
		const c2 = 240;
		const c3 = 90;
		const c4 = 90;
		const c5 = 90;

		const scale = availableWidth / (c1 + c2 + c3 + c4 + c5);

		return [
			{
				field: 'description',
				headerName: `${labels.questions}`,
				width: Math.round(c1 * scale),
				cellRendererSelector: (params) => {
					if (params.data.hasDescription) {
						return {
							component: AGGridRichTextCellRenderer
						};
					} else {
						return {
							component: AGGridLinkCellRenderer
						};
					}
				},
				rowSpan,
				cellClassRules: {
					'cell-height': (params) => params.data.isFreeText,
					'cell-span': (params) => params.data.rowSpan > 1,
					'cell-border-bottom': (params) => params.data.isLast,
					'cell-even': (params) => params.data.rowType === 'even',
					'cell-odd': (params) => params.data.rowType === 'odd'
				},
				cellDataType: false
			},
			{
				field: 'answers',
				headerName: `${labels.answers}`,
				cellClass: 'answerCompareResponses',
				cellRendererSelector: (params) => {
					const formBodyTypeId = params.data.formBodyTypeId;
					const showLink = params.data.showLink;
					const className = params.data.className || '';

					if (formBodyTypeId === formBodyType.Slider && className !== 'no-answer-provided') {
						return {
							component: AGGridSliderCellRenderer
						};
					} else if (showLink) {
						return {
							component: AGGridLinkCellRenderer
						};
					} else {
						return {
							component: AGGridRichTextCellRenderer
						};
					}
				},
				width: Math.round(c2 * scale),
				cellClassRules: {
					'cell-border': () => true,
					'cell-border-bottom': () => true,
					'cell-even': (params) => params.data.rowType === 'even',
					'cell-odd': (params) => params.data.rowType === 'odd'
				}
			},
			{
				field: 'countOfResponses',
				cellRenderer: AGGridCountOfResponsesCellRenderer,
				cellClass: 'countOfResponses',
				headerComponent: 'agColumnHeader',
				headerComponentParams: {
					displayName: labels.countOfResponses,
					noOfLines: 2,
					className: 'customTableHeader'
				},
				width: Math.round(c3 * scale),
				cellClassRules: {
					'cell-border': () => true,
					'cell-border-bottom': () => true,
					'cell-even': (params) => params.data.rowType === 'even',
					'cell-odd': (params) => params.data.rowType === 'odd'
				}
			},
			{
				field: 'openCommentCount',
				cellClass: 'openCommentCount',
				cellRenderer: AGGridCommentCountCellRenderer,
				cellRendererParams: {commentType: EngagementCommentStatus.open},
				width: Math.round(c4 * scale),
				headerComponent: 'agColumnHeader',
				headerComponentParams: {
					displayName: labels.openNotes,
					noOfLines: 2,
					className: 'customTableHeader'
				},
				cellClassRules: {
					'cell-border': () => true,
					'cell-border-bottom': () => true,
					'cell-even': (params) => params.data.rowType === 'even',
					'cell-odd': (params) => params.data.rowType === 'odd'
				}
			},
			{
				field: 'clearedCommentCount',
				cellClass: 'clearedCommentCount',
				cellRenderer: AGGridCommentCountCellRenderer,
				cellRendererParams: {commentType: EngagementCommentStatus.cleared},
				width: Math.round(c5 * scale),
				headerComponent: 'agColumnHeader',
				headerComponentParams: {
					displayName: labels.clearedNotes,
					noOfLines: 2,
					className: 'customTableHeader'
				},
				cellClassRules: {
					'cell-border': () => true,
					'cell-border-bottom': () => true,
					'cell-even': (params) => params.data.rowType === 'even',
					'cell-odd': (params) => params.data.rowType === 'odd'
				}
			}
		];
	}, [rowSpan]);

	const rowClassRules = useMemo(() => {
		return {
			'even-color': (params) => params.data.rowType === 'even',
			'odd-color': (params) => params.data.rowType === 'odd'
		};
	}, []);

	const defaultColDef = useMemo(() => {
		return {
			sortable: false,
			resizable: false,
			suppressMenu: true,
			wrapText: false,
			autoHeight: true,
			suppressMovable: true
		};
	}, []);

	const components = useMemo(() => {
		return {
			agColumnHeader: CustomHeader
		};
	}, []);

	const sectionName = useMemo(() => {
		if(section?.name){
			return section?.name + `${(section?.isDuplicate || section?.isCustom) && section?.customLabel ? ` - ${section?.customLabel}` : ''}`;
		}
		return '';
	}, [section]);

	const gridStyle = useMemo(() => ({height: '100%', width: '100%'}), []);

	const containerStyle = useMemo(() => ({width: '100%', height: 'calc(100% - var(--px-84))'}), []);

	if (isLoading) {
		return <LoadingIndicator view="blockview" show />;
	}

	return (
		<ReactiveTabRenamer>
			<StyledCompareResponsesV2DrillDownContainer className="StyledCompareResponsesV2DrillDownContainer">
				<section className="drill-down-container">
					<h2 className="knowledge-form-name motif-h2-default-regular">
						<EllipsesControl content={formData?.documentName} tooltip={formData?.documentName} noOfLines={1} />
					</h2>
					<section className="header-name">{header?.name}</section>
					<section className="section-name">
						{sectionName}
						{section?.isDuplicate && (
							<Icon className="duplicate-icon" src={MotifImageIcFilterNone24px} title={labels.duplicate} />
						)}
					</section>
					<section className="drill-down-table">
						<section style={containerStyle} className="AgGridContainerStyles">
							<section style={gridStyle} className="AGGridWrapperSection">
								{rowData && (
									<AgGridWrapper
										rowData={rowData}
										columnDefs={columnDefs}
										defaultColDef={defaultColDef}
										animateRows
										suppressContextMenu
										suppressRowClickSelection
										suppressCellFocus
										suppressRowTransform
										suppressHorizontalScroll
										noRowsOverlayComponent={noRowsOverlayComponent}
										components={components}
										rowClassRules={rowClassRules}
									/>
								)}
							</section>
						</section>
					</section>
				</section>
				<section className="navigation-buttons-container">
					<FormNavigationButton
						variant={'secondary'}
						type={'back'}
						engagementId={engagementId}
						taskId={query?.taskid}
						documentId={documentId}
						headerId={section?.formHeaderId}
						sectionId={sectionId}
						styleName={'BackButton'}
						isDrawerMode={isDrawerMode}
						isCompareResponsesView
					/>
					<FormNavigationButton
						variant={'primary-alt'}
						type={'next'}
						engagementId={engagementId}
						taskId={query?.taskid}
						documentId={documentId}
						headerId={section?.formHeaderId}
						sectionId={sectionId}
						styleName={'NextButton'}
						isDrawerMode={isDrawerMode}
						isCompareResponsesView
					/>
				</section>
			</StyledCompareResponsesV2DrillDownContainer>
		</ReactiveTabRenamer>
	);
}

export default CompareResponsesV2DrillDownContainer;

const StyledCompareResponsesV2DrillDownContainer = styled.section`
	display: flex;
	flex-direction: column;
	height: 100%;
	width: 100%;
	position: absolute;
	.drill-down-container {
		display: flex;
		height: calc(100% - var(--px-70));
		width: 100%;
		flex-direction: column;
		margin-bottom: var(--px-10);
		padding: var(--px-32);
		padding-bottom: var(--px-0);
		.knowledge-form-name {
			display: flex;
			font-size: var(--px-28);
			font-weight: 400;
			line-height: var(--px-42);
			margin: 0 0 var(--px-32) 0;
			color: var(--neutrals-900);
		}
		.header-name {
			display: flex;
			font-size: var(--px-24);
			font-weight: 400;
			line-height: var(--px-28);
			margin-bottom: var(--px-32);
			color: var(--neutrals-900);
		}
		.section-name {
			display: flex;
			font-size: var(--px-20);
			font-weight: 400;
			line-height: var(--px-24);
			margin-bottom: var(--px-24);
			color: var(--neutrals-900);
			align-items: center;
		}
		.duplicate-icon {
			display: flex;
			margin-left: var(--px-12);
		}
		.drill-down-table {
			display: flex;
			height: calc(100% - var(--px-210));
			width: 100%;
			.cell-span {
				background-color: var(--neutrals-00white);
				border-bottom-color: transparent;
			}
			.cell-border {
				border-left-color: var(--neutrals-200);
			}
			.cell-border-bottom {
				border-bottom-color: var(--neutrals-200);
			}
			.even-color {
				border-color: var(--neutrals-25);
			}
			.cell-even {
				background-color: var(--neutrals-25);
			}
			.odd-color {
				border-color: var(--neutrals-00white);
			}
			.cell-odd {
				background-color: var(--neutrals-00white);
			}
			.cell-height {
				height: 101% !important;
			}
			& > div {
				height: 100%;
				width: 100%;
			}
			.AgGridContainerStyles {
				height: 100% !important;
			}
			.AGGridWrapperSection {
				.ag-root-wrapper {
					.ag-root-wrapper-body {
						.ag-root {
							.ag-header {
								.ag-header-viewport {
									.ag-header-container {
										.ag-header-row {
											.ag-header-cell {
												.ag-header-cell-comp-wrapper {
													.ellipses-multilines,
													.ellipses {
														font-size: var(--px-14);
														font-weight: 400;
														line-height: var(--px-20);
														color: var(--neutrals-900);
													}
												}
												&:nth-child(3),
												&:nth-child(4),
												&:nth-child(5) {
													.ag-header-cell-comp-wrapper {
														.ellipses {
															justify-content: center;
														}
														.customTableHeader {
															justify-content: center;
														}
													}
												}
											}
										}
									}
								}
							}
							.ag-body-viewport {
								.ag-center-cols-viewport {
									.ag-center-cols-container {
										width: 100% !important;
										.ag-row {
											z-index: 1;
											.ag-cell {
												padding: 0;
												max-height: 100%;
												height: 100% !important;
												&[col-id='description'] {
													.createRichTextElementSpanWrapper {
														font-size: var(--px-14);
														font-weight: 400;
														line-height: var(--px-20);
														color: var(--neutrals-900);
														* {
															font-size: var(--px-14);
															font-weight: 400;
															line-height: var(--px-20);
														}
													}
												}
												&[col-id='answers'] {
													.createRichTextElementSpanWrapper {
														font-size: var(--px-12);
														font-weight: 300;
														line-height: var(--px-20);
														color: var(--neutrals-900);
														* {
															font-size: var(--px-12);
															font-weight: 300;
															line-height: var(--px-20);
														}
													}
													.trigger-link {
														.link,
														.link-label {
															font-size: var(--px-12);
															font-weight: 300;
															line-height: var(--px-20);
														}
													}
												}
												.ag-cell-wrapper {
													display: flex;
													align-items: center;
													justify-content: center;
													min-height: 100%;
													.ag-cell-value {
														margin: var(--px-12) var(--px-20) var(--px-8) var(--px-24);
														font-size: var(--px-12);
														font-weight: 300;
														line-height: var(--px-16);
														color: var(--neutrals-900);
														.ReactSliderBody {
															.HandleToolTipWrapper {
																.railStyle {
																	background-color: var(--neutrals-700);
																	height: var(--px-1);
																}
															}
															.slider-handles {
																.HandleWrapper {
																	.handleContainer {
																		.handleSlider {
																			background-color: var(--neutrals-900);
																		}
																	}
																}
															}
														}
													}
												}
												&.answerCompareResponses {
													.ag-cell-wrapper {
														.ag-cell-value {
															.StyledCompareTooltip {
																justify-content: flex-start;
															}
														}
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
	.navigation-buttons-container {
		display: flex;
		height: var(--px-84);
		padding-right: var(--px-36);
		align-items: center;
		justify-content: end;
		position: fixed;
		bottom: 0;
		width: 100%;
		background-color: var(--neutrals-00white);
		z-index: 9;
		border-top: var(--px-1) solid var(--neutrals-300);
		.StyledFormNavigationButtonV2 {
			&.NextButton {
				margin: 0 0 0 var(--px-16) !important;
				display: inline-flex;
				.motif-button {
					min-width: var(--px-90) !important;
					max-width: var(--px-350) !important;
					height: var(--px-44) !important;
					&.motif-button-large {
						line-height: unset;
					}
					.NextLabel {
						margin-right: var(--px-5);
					}
				}
			}
			&.BackButton {
				.motif-button {
					height: var(--px-44) !important;
				}
			}
		}
	}
`;
